/**
 * Emergent Marketplace — Bookings Portal
 * BookingsPortal.jsx
 * Architectural Layer: Presentation → Marketplace
 *
 * In-app bookings section. Handles:
 *   - Tier access gate (Silver+ required for Mode 1)
 *   - Practitioner browsing
 *   - Mode 1: In-app booking flow
 *   - Mode 3: External agent WebView overlay
 *   - Links out to public widget (Mode 2)
 *
 * Design: Deep forest, muted gold — reverent, unhurried.
 * Font: Crimson Pro (display) + Outfit (UI)
 */

import { useState, useEffect, useRef } from "react";
import {
  Lock, Star, Clock, Video, Phone, MapPin,
  ExternalLink, Calendar, CalendarCheck, ChevronRight, X,
  ArrowLeft, Check, Globe, Loader, CreditCard, Search, Heart
} from "lucide-react";
import { apiFetch } from "./api";
import DonationModal from "./DonationModal";
import PendingReviewsBanner from "@/components/PendingReviewsBanner";
import { SpotlightSlot } from "@/components/spotlights/SpotlightSlot";
import { useTaxEnabled, billingAddressFor, BillingZipField } from "@/components/BillingZip";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

import { stripePromise } from '@/lib/stripe';
import { CancellationTerms } from "@/components/CancellationTerms";
import { cssUrl } from "@/lib/cssUrl";
import { accentShades } from "@/lib/accent";
import { isAsleep, sleepNote } from "@/lib/sessionSleep";

// Uploaded banners are served from the backend, so a stored path needs the API origin.
const absUrl = (u) => (!u || u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);


// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Outfit:wght@300;400;500;600&display=swap');

  .bp-root {
    --forest:      #1b3a2d;
    --forest-mid:  #2d5a46;
    --forest-light:#4a8a68;
    --gold:        #c8a84a;
    --gold-light:  #e4c878;
    --gold-bg:     #faf5e8;
    --cream:       #f8f5ee;
    --cream-dark:  #eee8d8;
    --ink:         #1a1a18;
    --ink-mid:     #4a4a46;
    --ink-light:   #8a8a86;
    --border:      #ddd8cc;
    --surface:     #ffffff;
    --locked-bg:   #f4f0e8;
    font-family: 'Outfit', sans-serif;
    background: var(--cream);
    color: var(--ink);
    min-height: 100vh;
  }

  /* ── Tier gate ────────────────────────────────────────────────────────── */
  .bp-gate {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 80vh;
    padding: 40px 32px;
    text-align: center;
  }

  .bp-gate-lock {
    width: 80px; height: 80px;
    border-radius: 50%;
    background: var(--locked-bg);
    border: 2px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;
    color: var(--ink-light);
  }

  .bp-gate-title {
    font-family: 'Crimson Pro', serif;
    font-size: 28px;
    font-weight: 600;
    color: var(--ink);
    margin: 0 0 10px;
  }

  .bp-gate-body {
    font-size: 15px;
    color: var(--ink-mid);
    line-height: 1.65;
    max-width: 320px;
    margin: 0 auto 28px;
  }

  .bp-tier-pills {
    display: flex;
    gap: 8px;
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: 28px;
  }

  .bp-tier-pill {
    padding: 5px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .bp-tier-pill.locked   { background: var(--cream-dark); color: var(--ink-light); }
  .bp-tier-pill.silver   { background: #e8edf5; color: #3a5a8a; }
  .bp-tier-pill.gold     { background: var(--gold-bg); color: #8a6a10; }
  .bp-tier-pill.platinum { background: #f0eaf8; color: #6a3a9a; }
  .bp-tier-pill.comp     { background: #eaf5ea; color: #2a6a3a; }

  .bp-upgrade-btn {
    padding: 14px 32px;
    border-radius: 12px;
    background: var(--forest);
    border: none;
    color: white;
    font-family: 'Outfit', sans-serif;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
  }

  .bp-upgrade-btn:hover { background: var(--forest-mid); }

  /* ── Portal header ────────────────────────────────────────────────────── */
  .bp-header {
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    padding: 20px 24px;
    position: sticky;
    top: 0;
    z-index: 30;
  }

  .bp-header h1 {
    font-family: 'Crimson Pro', serif;
    font-size: 22px;
    font-weight: 600;
    color: var(--forest);
    margin: 0 0 2px;
  }

  .bp-header p { font-size: 13px; color: var(--ink-light); margin: 0; }

  /* ── Search bar ───────────────────────────────────────────────────────── */
  .bp-search-wrap {
    position: relative;
    margin-top: 14px;
  }

  .bp-search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--ink-light);
    pointer-events: none;
  }

  .bp-search-input {
    width: 100%;
    padding: 11px 14px 11px 40px;
    border: 1.5px solid var(--border);
    border-radius: 10px;
    background: var(--cream);
    font-family: 'Outfit', sans-serif;
    font-size: 14px;
    color: var(--ink);
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
  }

  .bp-search-input::placeholder { color: var(--ink-light); }

  .bp-search-input:focus {
    border-color: var(--forest-light);
    box-shadow: 0 0 0 3px rgba(27,58,45,0.08);
    background: var(--surface);
  }

  .bp-search-clear {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    color: var(--ink-light);
    padding: 4px;
    display: flex;
    align-items: center;
    border-radius: 50%;
    transition: color 0.15s;
  }

  .bp-search-clear:hover { color: var(--ink); }

  .bp-no-results {
    text-align: center;
    padding: 40px 24px;
    color: var(--ink-light);
  }

  .bp-no-results p:first-child {
    font-family: 'Crimson Pro', serif;
    font-size: 18px;
    color: var(--ink-mid);
    margin: 0 0 6px;
  }

  .bp-no-results p:last-child {
    font-size: 13px;
    margin: 0;
  }

  /* ── Practitioner list ────────────────────────────────────────────────── */
  .bp-list { padding: 16px 24px 40px; display: flex; flex-direction: column; gap: 12px; }

  .bp-practitioner-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 20px;
    cursor: pointer;
    transition: box-shadow 0.15s, border-color 0.15s;
  }

  .bp-practitioner-card:hover {
    border-color: var(--forest-light);
    box-shadow: 0 4px 20px rgba(0,0,0,0.07);
  }

  .bp-card-top {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    margin-bottom: 14px;
  }

  .bp-avatar {
    width: 56px; height: 56px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--forest), var(--forest-mid));
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Crimson Pro', serif;
    font-size: 22px;
    flex-shrink: 0;
    overflow: hidden;
  }

  .bp-avatar img {
    width: 100%; height: 100%;
    object-fit: cover;
  }

  .bp-card-info { flex: 1; }

  .bp-card-name {
    font-family: 'Crimson Pro', serif;
    font-size: 19px;
    font-weight: 600;
    color: var(--ink);
    margin: 0 0 3px;
  }

  .bp-card-bio {
    font-size: 13px;
    color: var(--ink-mid);
    line-height: 1.55;
    margin: 0 0 8px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .bp-bio-more {
    flex-shrink: 0;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
    color: var(--forest, #1a5a44);
    text-decoration: underline;
  }

  .bp-donate-inline {
    flex-shrink: 0;
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 14px;
    background: #fef9ea;
    border: 1px solid #e8d9a0;
    border-radius: 20px;
    color: #8a6a10;
    font-family: 'Outfit', sans-serif;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;
    white-space: nowrap;
  }

  .bp-donate-inline:hover {
    background: #fdf0c8;
    box-shadow: 0 2px 8px rgba(192,144,10,0.15);
  }

  .bp-card-meta {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;
  }

  .bp-rating {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: var(--gold);
    font-weight: 600;
  }

  /* Mode badge */
  .bp-mode-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
  }

  .bp-mode-inapp    { background: #eaf5ea; color: var(--forest); }
  .bp-mode-public   { background: var(--gold-bg); color: #7a5a10; }
  .bp-mode-external { background: #eaeaf5; color: #3a3a8a; }

  /* Session type chips */
  .bp-sessions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .bp-session-chip {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: var(--cream);
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 12px;
    color: var(--ink-mid);
  }

  .bp-session-price {
    font-weight: 700;
    color: var(--forest);
  }

  /* ── Booking detail view ──────────────────────────────────────────────── */
  .bp-detail {
    background: var(--cream);
    min-height: 100vh;
  }

  .bp-detail-header {
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    padding: 16px 24px;
    display: flex;
    align-items: center;
    gap: 12px;
    position: sticky;
    top: 0;
    z-index: 30;
  }

  .bp-back-btn {
    background: none; border: none;
    cursor: pointer; color: var(--ink-mid);
    display: flex; align-items: center;
    padding: 4px;
  }

  .bp-detail-title {
    font-family: 'Crimson Pro', serif;
    font-size: 19px;
    font-weight: 600;
    color: var(--ink);
    margin: 0;
  }

  .bp-detail-body { padding: 24px; }

  .bp-hero-banner {
    height: 170px;
    border-radius: 16px;
    background-color: var(--cream-dark);
    background-size: cover;
    background-position: center;
    margin-bottom: 16px;
  }

  .bp-practitioner-hero {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 24px;
    display: flex;
    gap: 16px;
    margin-bottom: 20px;
  }

  .bp-hero-avatar {
    width: 72px; height: 72px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--forest), var(--forest-mid));
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Crimson Pro', serif;
    font-size: 28px;
    flex-shrink: 0;
    overflow: hidden;
  }

  .bp-hero-avatar img {
    width: 100%; height: 100%;
    object-fit: cover;
  }

  .bp-hero-name {
    font-family: 'Crimson Pro', serif;
    font-size: 22px;
    font-weight: 600;
    margin: 0 0 6px;
    color: var(--ink);
  }

  .bp-hero-bio {
    font-size: 14px;
    color: var(--ink-mid);
    line-height: 1.6;
    margin: 0;
  }

  .bp-section-title {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--ink-light);
    margin: 0 0 12px;
  }

  /* Session type selector */
  .bp-session-options { display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; }

  .bp-session-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border: 1.5px solid var(--border);
    border-radius: 12px;
    cursor: pointer;
    background: var(--surface);
    transition: all 0.15s;
  }

  .bp-session-option:hover { border-color: var(--forest-light); }
  .bp-session-option.selected {
    border-color: var(--forest);
    background: rgba(27,58,45,0.03);
  }

  .bp-session-option-name {
    font-size: 15px;
    font-weight: 600;
    color: var(--ink);
    margin: 0 0 3px;
  }

  .bp-session-option-meta {
    font-size: 12px;
    color: var(--ink-light);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .bp-session-option-price {
    font-family: 'Crimson Pro', serif;
    font-size: 20px;
    font-weight: 600;
    color: var(--forest);
  }

  /* External booking panel */
  .bp-external-panel {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 24px;
    text-align: center;
    margin-bottom: 20px;
  }

  .bp-external-icon {
    width: 56px; height: 56px;
    border-radius: 50%;
    background: #eaeaf5;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    color: #3a3a8a;
  }

  .bp-external-title {
    font-family: 'Crimson Pro', serif;
    font-size: 20px;
    font-weight: 600;
    color: var(--ink);
    margin: 0 0 8px;
  }

  .bp-external-desc {
    font-size: 14px;
    color: var(--ink-mid);
    line-height: 1.6;
    margin: 0 0 20px;
  }

  .bp-external-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 13px 28px;
    border-radius: 11px;
    background: #3a3a8a;
    border: none;
    color: white;
    font-family: 'Outfit', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
  }

  .bp-external-btn:hover { opacity: 0.88; }

  /* A practitioner on Calendly/Acuity usually keeps one link per offering, so her
     session types are listed here and each one opens its own link. */
  .bp-ext-session {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    flex-wrap: wrap;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 13px;
    padding: 14px 16px;
    margin-bottom: 10px;
  }

  .bp-ext-session-name {
    font-family: 'Outfit', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: var(--ink);
    margin: 0 0 3px;
  }

  .bp-ext-session-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    font-size: 12px;
    color: var(--ink-light);
  }

  .bp-ext-session-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 10px 18px;
    border-radius: 10px;
    background: #3a3a8a;
    border: none;
    color: white;
    font-family: 'Outfit', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
    flex-shrink: 0;
  }

  .bp-ext-session-btn:hover { opacity: 0.88; }

  .bp-ext-note {
    font-size: 12px;
    color: var(--ink-light);
    line-height: 1.55;
    margin: 0 2px 18px;
  }

  .bp-ext-all-link {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-family: 'Outfit', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #3a3a8a;
    text-decoration: underline;
    margin-bottom: 18px;
  }

  /* Book button */
  .bp-book-btn {
    width: 100%;
    padding: 15px;
    border-radius: 12px;
    background: var(--forest);
    border: none;
    color: white;
    font-family: 'Outfit', sans-serif;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: background 0.15s;
    margin-top: 20px;
  }

  .bp-book-btn:hover { background: var(--forest-mid); }
  .bp-book-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .bp-payment-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 16px;
  }
  .bp-payment-title {
    font-family: 'Crimson Pro', serif;
    font-size: 17px;
    font-weight: 600;
    color: var(--forest);
    margin: 0 0 4px;
  }
  .bp-payment-subtitle {
    font-size: 13px;
    color: var(--ink-light);
    margin: 0 0 16px;
  }
  .bp-card-element-wrapper {
    background: white;
    border: 1.5px solid var(--border);
    border-radius: 8px;
    padding: 12px 14px;
    transition: border-color 0.2s;
  }
  .bp-card-element-wrapper:focus-within {
    border-color: var(--forest);
  }
  .bp-pay-btn {
    width: 100%;
    padding: 14px;
    border: none;
    border-radius: 10px;
    background: var(--forest);
    color: white;
    font-family: 'Outfit', sans-serif;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 16px;
  }
  .bp-pay-btn:hover { background: var(--forest-mid); }
  .bp-pay-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .bp-pay-error {
    color: #dc2626;
    font-size: 13px;
    margin-top: 8px;
  }

  /* ── WebView overlay (Mode 3) ─────────────────────────────────────────── */
  .bp-webview-overlay {
    position: fixed;
    inset: 0;
    z-index: 200;
    background: var(--cream);
    display: flex;
    flex-direction: column;
    animation: bp-slidein 0.25s ease;
  }

  @keyframes bp-slidein {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }

  .bp-webview-bar {
    background: var(--forest);
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
  }

  .bp-webview-info {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .bp-webview-agent {
    font-size: 12px;
    color: rgba(255,255,255,0.7);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .bp-webview-name {
    font-family: 'Crimson Pro', serif;
    font-size: 17px;
    color: white;
    font-weight: 600;
  }

  .bp-webview-close {
    background: rgba(255,255,255,0.15);
    border: none;
    border-radius: 50%;
    width: 32px; height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    cursor: pointer;
    transition: background 0.15s;
  }

  .bp-webview-close:hover { background: rgba(255,255,255,0.25); }

  .bp-webview-frame {
    flex: 1;
    border: none;
    width: 100%;
  }

  .bp-webview-mock {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: var(--ink-mid);
    padding: 32px;
    text-align: center;
  }

  /* ── Confirm state ────────────────────────────────────────────────────── */
  .bp-confirm {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 28px;
    text-align: center;
  }

  .bp-confirm-circle {
    width: 80px; height: 80px;
    border-radius: 50%;
    background: #eaf5ea;
    border: 2px solid #b8d8c0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;
    color: var(--forest);
  }

  .bp-confirm-title {
    font-family: 'Crimson Pro', serif;
    font-size: 26px;
    font-weight: 600;
    color: var(--ink);
    margin: 0 0 10px;
  }

  .bp-confirm-body {
    font-size: 14px;
    color: var(--ink-mid);
    line-height: 1.65;
    margin: 0 0 24px;
    max-width: 320px;
  }
`;

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_PRACTITIONERS = [
  {
    id: "v1",
    vendor_id: "v1",
    display_name: "Maya Chen",
    bio: "Certified spiritual life coach and energy healer. 10+ years guiding souls through conscious awakening and transformation.",
    avatar_url: null,
    modes_enabled: ["in_app"],
    rating_avg: 4.9,
    rating_count: 84,
    session_types: [
      { id: "s1", title: "Clarity Session",       duration_minutes: 60,  price: 120 },
      { id: "s2", title: "Deep Dive Integration",  duration_minutes: 90,  price: 175 },
    ],
    external_url: null,
    external_agent: null,
  },
  {
    id: "v2",
    vendor_id: "v2",
    display_name: "James O'Brien",
    bio: "Jungian-trained shadow work facilitator and meditation teacher. Specializing in conscious relationship and masculine embodiment.",
    avatar_url: null,
    modes_enabled: ["in_app", "external"],
    rating_avg: 5.0,
    rating_count: 41,
    session_types: [
      { id: "s3", title: "Shadow Work Intro",   duration_minutes: 60, price: 95 },
    ],
    external_url: "https://calendly.com/james-stillpoint",
    external_agent: "Calendly",
  },
  {
    id: "v3",
    vendor_id: "v3",
    display_name: "Aria Santos",
    bio: "Sound healer and channeled guide. Sessions combine tuning forks, vocal toning, and intuitive guidance for deep energetic recalibration.",
    avatar_url: null,
    modes_enabled: ["public"],
    public_enabled: true,
    rating_avg: 4.8,
    rating_count: 117,
    session_types: [
      { id: "s4", title: "Sound Bath (1:1)",    duration_minutes: 45, price: 85 },
      { id: "s5", title: "Channeled Reading",   duration_minutes: 30, price: 60 },
    ],
    external_url: null,
    external_agent: null,
  },
];

const MOCK_USER = { id: "u1", tier: "silver" }; // change to "bronze" to test gate

const MODE_CONFIG = {
  in_app:   { label: "Book in-app",      icon: Calendar },
  public:   { label: "Public booking",   icon: Globe },
  external: { label: "External booking", icon: ExternalLink },
};

// ─── Tier Gate ────────────────────────────────────────────────────────────────

function TierGate({ onUpgrade }) {
  return (
    <div className="bp-gate">
      <div className="bp-gate-lock"><Lock size={32} /></div>
      <h2 className="bp-gate-title">Sessions for Silver members & above</h2>
      <p className="bp-gate-body">
        1:1 coaching and healing sessions are available to Silver, Gold, Platinum, and Comp members. Upgrade your membership to unlock the full Bookings portal.
      </p>
      <div className="bp-tier-pills">
        <span className="bp-tier-pill locked">Free</span>
        <span className="bp-tier-pill locked">Bronze</span>
        <span className="bp-tier-pill silver">Silver ✓</span>
        <span className="bp-tier-pill gold">Gold ✓</span>
        <span className="bp-tier-pill platinum">Platinum ✓</span>
        <span className="bp-tier-pill comp">Free-Comp ✓</span>
      </div>
      <button className="bp-upgrade-btn" onClick={onUpgrade}>
        Upgrade membership
      </button>
    </div>
  );
}

// ─── WebView Overlay (Mode 3) ─────────────────────────────────────────────────

function ExternalWebView({ practitioner, url, label, onClose }) {
  const [iframeError, setIframeError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const target = url || practitioner.external_url;
  const agent = label || practitioner.external_agent;

  // The old check read `iframe.contentDocument` and treated null as "this site blocks framing".
  // For ANY cross-origin page that property is null by design and does not throw — so every
  // external booking page was declared broken and members were sent down a dead end
  // ("Acuity can't be displayed inline") even though Acuity frames perfectly well.
  // A browser cannot see whether a frame was refused, so the only honest client-side signal is
  // that nothing ever loaded. Whether a site truly forbids framing is answered server-side by
  // /external-booking/embeddable, which decides before this overlay is ever opened.
  useEffect(() => {
    if (!target || loaded) return;
    const timer = setTimeout(() => setIframeError(true), 12000);
    return () => clearTimeout(timer);
  }, [target, loaded]);

  const handleOpenExternal = () => {
    window.open(target, '_blank', 'noopener');
  };

  return (
    <div className="bp-webview-overlay">
      <div className="bp-webview-bar">
        <div className="bp-webview-info">
          <div>
            <div className="bp-webview-agent">
              {agent || "External booking"} — {practitioner.display_name}
            </div>
          </div>
        </div>
        <button className="bp-webview-close" onClick={onClose}>
          <X size={16} />
        </button>
      </div>

      {target && !iframeError ? (
        <iframe
          className="bp-webview-frame"
          src={target}
          title={`${practitioner.display_name} booking`}
          style={{ width: "100%", flex: 1, border: "none" }}
          allow="payment"
          onError={() => setIframeError(true)}
          onLoad={() => setLoaded(true)}
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: 16, padding: 32 }}>
          <Globe size={36} style={{ opacity: 0.3 }} />
          <p style={{ fontFamily: "'Crimson Pro', serif", fontSize: 18, color: "var(--ink)", margin: 0, textAlign: "center" }}>
            {iframeError
              ? `${agent || "This booking site"} can't be displayed inline`
              : "No external booking URL configured"}
          </p>
          {target && (
            <>
              <p style={{ fontSize: 13, color: "var(--ink-light)", margin: 0, textAlign: "center" }}>
                Click below to open their booking page in a new tab
              </p>
              <button
                onClick={handleOpenExternal}
                data-testid="open-external-booking-btn"
                style={{
                  marginTop: 8, padding: "12px 32px", borderRadius: 10,
                  background: "var(--forest)", border: "none", color: "white",
                  fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer",
                }}
              >
                Open {agent || "Booking Page"} →
              </button>
            </>
          )}
          <button
            onClick={onClose}
            style={{
              marginTop: 8, padding: "10px 24px", borderRadius: 10,
              background: "transparent", border: "1px solid var(--border)", color: "var(--ink)",
              fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 500, cursor: "pointer",
            }}
          >
            ← Back
          </button>
        </div>
      )}
    </div>
  );
}



// ─── Stripe Payment Form ─────────────────────────────────────────────────────

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const BP_LOCATION_LABELS = {
  video:     "Video call",
  in_person: "In person",
  phone:     "Phone call",
  custom:    "Details from your practitioner",
};

// The town for an in-person session; the street address waits for the confirmation.
const bpLocationLine = (st) => {
  const mode = st?.location_mode || "video";
  if (mode === "in_person") {
    return st?.location_city
      ? `In person · ${st.location_city}`
      : "In person — address sent when your session is confirmed";
  }
  return BP_LOCATION_LABELS[mode];
};

const bpTzLabel = (tz) => {
  if (!tz) return "";
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "long" })
      .formatToParts(new Date())
      .find(p => p.type === "timeZoneName")?.value || tz.replace(/_/g, " ");
  } catch {
    return tz.replace(/_/g, " ");
  }
};

function StripePaymentInner({ clientSecret, amount, breakdown, sessionTitle, savedCard, cancellationPolicy, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  // A card they already saved should not have to be typed again — the tester hit exactly that.
  const [useNewCard, setUseNewCard] = useState(!savedCard);
  const payingWithSaved = !!savedCard && !useNewCard;

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe) return;
    if (!payingWithSaved && !elements) return;

    setProcessing(true);
    setError(null);

    const { error: confirmError } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: payingWithSaved
        ? savedCard.id
        : { card: elements.getElement(CardElement) },
    });

    if (confirmError) {
      setError(confirmError.message);
      setProcessing(false);
    } else {
      onSuccess();
    }
  };

  const cardStyle = {
    style: {
      base: {
        fontSize: '15px',
        color: '#1b3a2d',
        fontFamily: "'Outfit', sans-serif",
        '::placeholder': { color: '#9ca3af' },
      },
      invalid: { color: '#dc2626' },
    },
  };

  return (
    <form onSubmit={handlePay}>
      <div className="bp-payment-card">
        <p className="bp-payment-title">
          <CreditCard size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: -2 }} />
          Payment for {sessionTitle}
        </p>
        <p className="bp-payment-subtitle">
          ${(amount / 100).toFixed(2)} — Your card will be held until the practitioner confirms.
        </p>
        {breakdown && breakdown.processing_fee > 0 && (
          <p className="bp-payment-subtitle" style={{ fontSize: 12, marginTop: -4 }}
             data-testid="booking-charge-breakdown">
            ${round2(breakdown.price).toFixed(2)} session
            {breakdown.tax > 0 && ` + $${round2(breakdown.tax).toFixed(2)} sales tax`}
            {` + $${round2(breakdown.processing_fee).toFixed(2)} card processing (3% + $0.30)`}
          </p>
        )}
        {payingWithSaved ? (
          <div className="bp-card-element-wrapper" data-testid="booking-saved-card">
            <span style={{ fontSize: 14 }}>
              <CreditCard size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: -2 }} />
              {savedCard.brand} ••••{savedCard.last4} — expires {savedCard.exp_month}/{savedCard.exp_year}
            </span>
            <button type="button" onClick={() => { setUseNewCard(true); setError(null); }}
              data-testid="booking-use-new-card-btn"
              style={{
                marginLeft: 10, background: 'transparent', border: 'none', padding: 0,
                color: 'var(--ink)', textDecoration: 'underline', fontSize: 13, cursor: 'pointer',
                fontFamily: "'Outfit', sans-serif",
              }}>
              Use a different card
            </button>
          </div>
        ) : (
          <div className="bp-card-element-wrapper">
            <CardElement options={cardStyle} />
            {savedCard && (
              <button type="button" onClick={() => { setUseNewCard(false); setError(null); }}
                data-testid="booking-use-saved-card-btn"
                style={{
                  marginTop: 8, background: 'transparent', border: 'none', padding: 0,
                  color: 'var(--ink)', textDecoration: 'underline', fontSize: 13, cursor: 'pointer',
                  fontFamily: "'Outfit', sans-serif",
                }}>
                Use my saved {savedCard.brand} ••••{savedCard.last4} instead
              </button>
            )}
          </div>
        )}
        {error && <p className="bp-pay-error" data-testid="payment-error">{error}</p>}
        {/* A payer reads what is next to the pay button. */}
        <CancellationTerms policy={cancellationPolicy} compact />
      </div>
      <button
        type="submit"
        className="bp-pay-btn"
        disabled={!stripe || processing}
        data-testid="payment-submit-btn"
      >
        {processing
          ? <><Loader size={16} style={{ animation: "spin 1s linear infinite" }} /> Processing...</>
          : <><CreditCard size={16} /> Confirm & hold ${(amount / 100).toFixed(2)}</>
        }
      </button>
      <button
        type="button"
        onClick={onCancel}
        style={{
          width: '100%', marginTop: 8, padding: 10, background: 'none',
          border: 'none', color: 'var(--ink-light)', fontSize: 13,
          cursor: 'pointer', fontFamily: "'Outfit', sans-serif",
        }}
        data-testid="payment-cancel-btn"
      >
        Cancel
      </button>
    </form>
  );
}

function PaymentForm({ clientSecret, amount, breakdown, sessionTitle, cancellationPolicy, onSuccess, onCancel }) {
  const [savedCard, setSavedCard] = useState(null);
  // The card list arrives after the first render, and `useNewCard` was seeded from it — so the
  // form opened asking for a card number even when a saved card existed. Wait for the answer.
  const [cardsLoaded, setCardsLoaded] = useState(false);

  useEffect(() => {
    apiFetch("/bookings/saved-cards")
      .then((cards) => setSavedCard(cards?.[0] || null))
      .catch(() => {})
      .finally(() => setCardsLoaded(true));
  }, []);

  if (!clientSecret || !cardsLoaded) return null;
  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <StripePaymentInner
        clientSecret={clientSecret}
        amount={amount}
        breakdown={breakdown}
        sessionTitle={sessionTitle}
        savedCard={savedCard}
        cancellationPolicy={cancellationPolicy}
        onSuccess={onSuccess}
        onCancel={onCancel}
      />
    </Elements>
  );
}

// ─── Booking Calendar ────────────────────────────────────────────────────────

function BookingCalendar({ availableDates, selectedDate, onSelectDate }) {
  const [viewMonth, setViewMonth] = useState(() => new Date());

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const monthName = viewMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const prevMonth = () => setViewMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setViewMonth(new Date(year, month + 1, 1));

  return (
    <div data-testid="booking-calendar" style={{ maxWidth: 360, margin: '0 auto' }}>
      {/* Month nav */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 12,
      }}>
        <button
          onClick={prevMonth}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--ink-mid)' }}
        >
          <ChevronRight size={18} style={{ transform: 'rotate(180deg)' }} />
        </button>
        <span style={{
          fontFamily: "'Crimson Pro', serif", fontSize: 16, fontWeight: 600, color: 'var(--ink)'
        }}>
          {monthName}
        </span>
        <button
          onClick={nextMonth}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--ink-mid)' }}
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Day headers */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4,
        textAlign: 'center', marginBottom: 4,
      }}>
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
          <div key={d} style={{
            fontSize: 11, fontWeight: 600, color: 'var(--ink-light)',
            padding: '4px 0', fontFamily: "'Outfit', sans-serif",
          }}>{d}</div>
        ))}
      </div>

      {/* Day grid */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4,
        textAlign: 'center',
      }}>
        {days.map((day, idx) => {
          if (!day) return <div key={`e-${idx}`} />;

          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const dateObj = new Date(year, month, day);
          const isPast = dateObj < today;
          const isAvailable = availableDates.has(dateStr) && !isPast;
          const isSelected = selectedDate === dateStr;

          return (
            <button
              key={dateStr}
              onClick={() => isAvailable && onSelectDate(dateStr)}
              disabled={!isAvailable}
              data-testid={`cal-day-${dateStr}`}
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: isSelected ? '2px solid var(--forest)' : '1.5px solid transparent',
                background: isSelected
                  ? 'rgba(27,58,45,0.12)'
                  : isAvailable
                    ? 'rgba(27,58,45,0.05)'
                    : 'transparent',
                color: isSelected
                  ? 'var(--forest)'
                  : isAvailable
                    ? 'var(--ink)'
                    : isPast
                      ? 'var(--border)'
                      : 'var(--ink-light)',
                fontFamily: "'Outfit', sans-serif",
                fontSize: 13,
                fontWeight: isSelected ? 700 : isAvailable ? 500 : 400,
                cursor: isAvailable ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.12s',
                position: 'relative',
                margin: '0 auto',
              }}
            >
              {day}
              {isAvailable && !isSelected && (
                <span style={{
                  position: 'absolute', bottom: 2,
                  width: 4, height: 4, borderRadius: '50%',
                  background: 'var(--forest-light)',
                }} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Practitioner Detail + Booking ────────────────────────────────────────────

function PractitionerDetail({ practitioner, onBack }) {
  // Her banner and her accent colour, set in Availability & Booking, apply to her block here
  // just as they do on her public page — the member view used to show only her bio.
  const accent = practitioner.widget_accent || '#1b3a2d';
  const shades = accentShades(accent);
  const accentVars = {
    '--forest':       shades.base,
    '--forest-mid':   shades.light,
    '--forest-light': shades.pale,
  };
  const [selectedSession, setSelectedSession] = useState(null);
  const [showWebView, setShowWebView] = useState(null);   // null | { url, label }
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [availableDates, setAvailableDates] = useState(new Set());
  // Payment state
  const [paymentStep, setPaymentStep] = useState(null); // null | { clientSecret, amount, title, bookingId, breakdown }
  const [chargedTotal, setChargedTotal] = useState(null); // what the card was actually charged
  // Batch booking state
  const [selectedSlots, setSelectedSlots] = useState([]); // array of slot objects
  const [saveCard, setSaveCard] = useState(false);
  const taxEnabled = useTaxEnabled();
  const [billingZip, setBillingZip] = useState("");
  const [savedCards, setSavedCards] = useState([]);
  const [batchResult, setBatchResult] = useState(null);
  // Package state
  const [usePackage, setUsePackage] = useState(null); // null | 'buy' | package_id
  const [sessionDetails, setSessionDetails] = useState(null);
  const [myPackages, setMyPackages] = useState([]);
  const [purchasingPackage, setPurchasingPackage] = useState(false);

  const hasInApp    = (practitioner.modes_enabled || []).includes("in_app");
  const hasExternal = (practitioner.modes_enabled || []).includes("external");
  const hasPublic   = (practitioner.modes_enabled || []).includes("public");
  const hasSessions = (practitioner.session_types || []).some(st => !st.external_url);
  // Sessions she books on her own system. Each carries its own scheduling link.
  const externalSessions = (practitioner.session_types || []).filter(st => st.external_url);

  // Load saved cards and packages on mount
  useEffect(() => {
    apiFetch("/bookings/saved-cards").then(setSavedCards).catch(() => {});
    apiFetch("/bookings/my-packages").then(setMyPackages).catch(() => {});
  }, []);

  // Acuity and some other schedulers refuse to be shown inside another page, so the overlay
  // would open onto a blank wall and make the member click again. Ask the server which of this
  // practitioner's links can be framed, so the FIRST click already goes to the right place.
  const [embeddable, setEmbeddable] = useState({});   // url -> true | false
  useEffect(() => {
    if (!hasExternal) return;
    const urls = [practitioner.external_url, ...externalSessions.map(s => s.external_url)]
      .filter(Boolean);
    [...new Set(urls)].slice(0, 8).forEach(url => {
      apiFetch(`/external-booking/embeddable?url=${encodeURIComponent(url)}`)
        .then(r => setEmbeddable(prev => ({ ...prev, [url]: r.embeddable })))
        .catch(() => {});
    });
  }, [hasExternal, practitioner.vendor_id]);

  const opensInNewTab = (url) => embeddable[url] === false;

  const openBooking = (url, label) => {
    if (!url) return;
    if (opensInNewTab(url)) {
      window.open(url, "_blank", "noopener");
      return;
    }
    setShowWebView({ url, label });
  };

  // On mount (or when session changes), fetch available slots for the next 30 days
  useEffect(() => {
    if (!hasInApp) return;
    const duration = selectedSession
      ? (selectedSession.total_duration || selectedSession.healing_duration || selectedSession.duration_minutes || 60)
      : 60;
    const today = new Date();
    const fromDate = today.toISOString().split('T')[0];
    const endDate = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    let url = `/availability/${practitioner.vendor_id}/slots?from_date=${fromDate}&to_date=${endDate}&duration_minutes=${duration}`;
    if (selectedSession) url += `&session_type_id=${selectedSession.id}`;

    setLoadingSlots(true);
    setSelectedDate(null);
    setSelectedSlot(null);
    apiFetch(url)
      .then(data => {
        const dates = new Set((data.slots || []).map(s => s.date_str));
        setAvailableDates(dates);
        setSlots(data.slots || []);
      })
      .catch(() => { setAvailableDates(new Set()); setSlots([]); })
      .finally(() => setLoadingSlots(false));
  }, [hasInApp, practitioner.vendor_id, selectedSession]);

  const slotsForDate = selectedDate
    ? slots.filter(s => s.date_str === selectedDate)
    : [];

  const toggleSlot = (slot) => {
    // A basket line carries its own key/session type — removing it must not be
    // rekeyed against whichever session type happens to be selected right now.
    const typeId = slot.key ? slot.session_type_id : (selectedSession?.id || null);
    const key = slot.key || `${slot.datetime_utc}|${typeId || 'general'}`;
    setSelectedSlots(prev => {
      const exists = prev.find(s => s.key === key);
      if (exists) return prev.filter(s => s.key !== key);
      return [...prev, {
        ...slot,
        key,
        session_type_id: typeId,
        session_name: selectedSession?.name || 'General Consultation',
        price: selectedSession?.price || 0,
        duration_minutes: selectedSession
          ? (selectedSession.total_duration || selectedSession.healing_duration || selectedSession.duration_minutes || 60)
          : 60,
        only_session_for_day: !!selectedSession?.only_session_for_day,
      }];
    });
    setSelectedSlot(slot); // keep single select for visual feedback
  };

  // The slot picker only knows about bookings already in the DB, so it must
  // also respect what's sitting in the basket: a whole-day session blocks its
  // date, and nothing may overlap anything else.
  const slotConflict = (slot) => {
    if (isSlotSelected(slot)) return null;
    const start = new Date(slot.datetime_utc);
    const duration = selectedSession
      ? (selectedSession.total_duration || selectedSession.healing_duration || selectedSession.duration_minutes || 60)
      : 60;
    const end = new Date(start.getTime() + duration * 60000);
    const isFullDay = !!selectedSession?.only_session_for_day;

    for (const s of selectedSlots) {
      const sStart = new Date(s.datetime_utc);
      const sEnd = new Date(sStart.getTime() + (s.duration_minutes || 60) * 60000);
      const sameDay = sStart.toDateString() === start.toDateString();
      if (sameDay && s.only_session_for_day) {
        return `${s.session_name} takes this whole day`;
      }
      if (sameDay && isFullDay) {
        return `${selectedSession.name} needs the whole day, and ${s.session_name} is already on it`;
      }
      if (start < sEnd && sStart < end) {
        return `Overlaps ${s.session_name} at ${s.time_label}`;
      }
    }
    return null;
  };

  const isSlotSelected = (slot) => selectedSlots.some(
    s => s.datetime_utc === slot.datetime_utc && s.session_type_id === (selectedSession?.id || null)
  );

  const perSessionPrice = selectedSession?.price || 0;
  const totalPrice = selectedSlots.reduce((sum, s) => sum + (s.price || 0), 0);
  // The cart showed the session prices as "Total" and the card was then charged 3% + $0.30
  // more (owner, Jun 2026). Same rule as utils/processing_fee.py; the server's figure wins
  // once the booking exists (charge_breakdown / charge_total).
  const estProcessing = (net) => (net > 0 ? Math.round((net * 0.03 + 0.30) * 100) / 100 : 0);
  const cartProcessing = estProcessing(totalPrice);

  const bookSession = async () => {
    if (selectedSlots.length === 0 && usePackage !== 'buy') return;
    setBooking(true);
    try {
      // Case 1: Purchasing a package
      if (usePackage === 'buy' && selectedSession) {
        setPurchasingPackage(true);
        const result = await apiFetch("/bookings/purchase-package", {
          method: "POST",
          body: JSON.stringify({
            vendor_id: practitioner.vendor_id,
            session_type_id: selectedSession.id,
            save_card: saveCard,
            billing_address: billingAddressFor(billingZip),
          }),
        });
        if (result.requires_payment && result.client_secret) {
          setPaymentStep({
            clientSecret: result.client_secret,
            amount: (result.charge_total ?? result.price_total) * 100,
            title: `${result.sessions_total}-session package: ${result.session_type_name}`,
            packageId: result.id,
            breakdown: {
              price: result.price_total,
              tax: result.tax_amount || 0,
              processing_fee: Math.max(
                0, round2((result.charge_total ?? result.price_total) - result.price_total - (result.tax_amount || 0))),
            },
          });
        } else {
          setConfirmed(true);
          apiFetch("/bookings/my-packages").then(setMyPackages).catch(() => {});
        }
        setPurchasingPackage(false);
        setBooking(false);
        return;
      }

      // Case 2: Using a package credit (single booking, no payment)
      if (usePackage && usePackage !== 'buy' && selectedSlots.length === 1) {
        const slot = selectedSlots[0];
        const result = await apiFetch("/bookings/use-package-credit", {
          method: "POST",
          body: JSON.stringify({
            package_id: usePackage,
            scheduled_at: slot.datetime_utc,
            timezone: practitioner.timezone || "America/New_York",
          }),
        });
        // Refresh packages
        apiFetch("/bookings/my-packages").then(setMyPackages).catch(() => {});
        setConfirmed(true);
        setBooking(false);
        return;
      }

      // Case 3: Single booking (existing flow)
      if (selectedSlots.length === 1) {
        const slot = selectedSlots[0];
        const body = {
          vendor_id: practitioner.vendor_id,
          scheduled_at: slot.datetime_utc,
          timezone: practitioner.timezone || "America/New_York",
          billing_address: billingAddressFor(billingZip),
        };
        if (slot.session_type_id) {
          body.session_type_id = slot.session_type_id;
        } else {
          body.duration_minutes = 60;
        }
        const result = await apiFetch("/bookings", {
          method: "POST",
          body: JSON.stringify(body),
        });

        if (result.requires_payment && result.client_secret) {
          setPaymentStep({
            clientSecret: result.client_secret,
            amount: (result.charge_total ?? slot.price ?? perSessionPrice) * 100,
            title: result.session_title || slot.session_name || "Session",
            bookingId: result.id,
            breakdown: result.charge_breakdown || {
              price: slot.price ?? perSessionPrice,
              tax: 0,
              processing_fee: estProcessing(slot.price ?? perSessionPrice),
            },
          });
        } else {
          setConfirmed(true);
        }
      } else {
        // Case 4: Batch booking
        const body = {
          vendor_id: practitioner.vendor_id,
          items: selectedSlots.map(s => ({
            scheduled_at: s.datetime_utc,
            session_type_id: s.session_type_id,
          })),
          timezone: practitioner.timezone || "America/New_York",
          save_card: saveCard,
          billing_address: billingAddressFor(billingZip),
          duration_minutes: 60,
        };
        const result = await apiFetch("/bookings/batch", {
          method: "POST",
          body: JSON.stringify(body),
        });
        setBatchResult(result);

        if (result.requires_payment && result.client_secret) {
          setPaymentStep({
            clientSecret: result.client_secret,
            amount: (result.charge_total ?? result.total_price) * 100,
            title: result.session_count > 1
              ? `${result.session_count} sessions with ${practitioner.display_name || 'your practitioner'}`
              : result.session_title,
            bookingIds: result.booking_ids,
            breakdown: {
              price: result.total_price,
              tax: 0,
              processing_fee: Math.max(
                0, round2((result.charge_total ?? result.total_price) - result.total_price)),
            },
          });
        } else {
          setConfirmed(true);
        }
      }
    } catch (e) {
      alert(e.message || "Booking failed");
    } finally {
      setBooking(false);
    }
  };

  if (confirmed) {
    return (
      <>
        <style>{styles}</style>
        <div className="bp-detail">
          <div className="bp-detail-header">
            <button className="bp-back-btn" onClick={onBack} data-testid="booking-back-btn"><ArrowLeft size={20} /></button>
            <h2 className="bp-detail-title">Booking confirmed</h2>
          </div>
          <div className="bp-confirm">
            <div className="bp-confirm-circle"><Check size={36} strokeWidth={2.5} /></div>
            <h3 className="bp-confirm-title">You're booked</h3>
            <p className="bp-confirm-body">
              {selectedSlots.length > 1
                ? <>
                    <strong>{selectedSlots.length} sessions</strong> with <strong>{practitioner.display_name || 'Practitioner'}</strong> have been requested.
                    {totalPrice > 0 ? " Your payment is held and will be captured when the practitioner confirms." : " You'll receive confirmations as they accept."}
                  </>
                : <>
                    Your session with <strong>{practitioner.display_name || 'Practitioner'}</strong> has been requested.
                    {perSessionPrice > 0 ? " Your payment is held and will be captured when the practitioner confirms." : " You'll receive a confirmation once they accept."}
                  </>
              }
            </p>
            <div style={{ margin: '12px 0 16px', textAlign: 'left', width: '100%', maxWidth: 340 }} data-testid="booking-confirmed-list">
              {selectedSlots.map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: 'var(--ink-mid)', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <CalendarCheck size={13} style={{ marginTop: 3, flexShrink: 0 }} />
                  <span>
                    <strong style={{ color: 'var(--ink)' }}>{s.session_name}</strong><br />
                    {s.date_label} at {s.time_label}
                    {s.price > 0 ? ` · $${s.price.toFixed(2)}` : ' · Free'}
                  </span>
                </div>
              ))}
              {totalPrice > 0 && (
                <div style={{ fontSize: 14, fontWeight: 600, marginTop: 8, color: 'var(--forest)' }} data-testid="booking-confirmed-total">
                  Charged: ${(chargedTotal ?? (totalPrice + estProcessing(totalPrice))).toFixed(2)}
                  <span style={{ display: 'block', fontSize: 11, fontWeight: 400, color: 'var(--ink-light)', marginTop: 2 }}>
                    ${totalPrice.toFixed(2)} in sessions + card processing (3% + $0.30)
                  </span>
                </div>
              )}
            </div>
            <button className="bp-upgrade-btn" onClick={onBack} data-testid="booking-back-to-list-btn">Back to practitioners</button>
            <p style={{ fontSize: 12, color: 'var(--ink-light)', marginTop: 14, maxWidth: 360, lineHeight: 1.5, fontStyle: 'italic' }} data-testid="booking-tip-notice">
              After your session, you'll have the opportunity to leave a review and tip your practitioner. Tipping is not mandatory, but it is appreciated.
            </p>
          </div>
        </div>
      </>
    );
  }

  // Helper: check if a calendar date has available slots
  const isDayAvailable = (day) => {
    const dateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
    return availableDates.has(dateStr);
  };

  return (
    <>
      <style>{styles}</style>
      <div className="bp-detail" style={accentVars}>
        {sessionDetails && (
          <div
            onClick={() => setSessionDetails(null)}
            data-testid="session-details-panel"
            style={{
              position: 'fixed', inset: 0, zIndex: 70, padding: 20,
              background: 'rgba(16,18,16,0.55)', backdropFilter: 'blur(3px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: 'var(--cream, #fbf9f4)', border: '1px solid var(--border)',
                borderRadius: 16, padding: '24px 22px 20px', maxWidth: 460, width: '100%',
                maxHeight: '82vh', overflowY: 'auto', position: 'relative',
                boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
              }}
            >
              <button
                onClick={() => setSessionDetails(null)}
                aria-label="Close"
                data-testid="session-details-close"
                style={{
                  position: 'absolute', top: 12, right: 12, width: 28, height: 28,
                  borderRadius: 8, border: '1px solid var(--border)', background: 'none',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--ink-mid)',
                }}
              >
                <X size={16} />
              </button>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 20, fontWeight: 600,
                          color: 'var(--ink)', margin: '0 28px 4px 0' }}>
                {sessionDetails.name || sessionDetails.title}
              </p>
              <p style={{ fontSize: 12, color: 'var(--ink-light)', margin: '0 0 14px' }}>
                {sessionDetails.healing_duration || sessionDetails.duration_minutes || 60} min ·{" "}
                {bpLocationLine(sessionDetails)} ·{" "}
                {sessionDetails.price > 0 ? `$${sessionDetails.price}` : 'Free'}
              </p>
              {String(sessionDetails.description || '').split(/\n{1,}/).map((para, i) => (
                <p key={i} style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--ink-mid)', margin: '0 0 12px' }}>
                  {para}
                </p>
              ))}
              <button
                className="bp-book-btn"
                style={{ width: '100%', marginTop: 6 }}
                onClick={() => {
                  setSelectedSession(sessionDetails);
                  setUsePackage(null);
                  setSessionDetails(null);
                }}
                data-testid="session-details-choose"
              >
                Choose this session
              </button>
            </div>
          </div>
        )}
        <div className="bp-detail-header">
          <button className="bp-back-btn" onClick={onBack} data-testid="booking-detail-back-btn"><ArrowLeft size={20} /></button>
          <h2 className="bp-detail-title">{practitioner.display_name || 'Practitioner'}</h2>
        </div>

        <div className="bp-detail-body">
          {/* Her own banner and colour, exactly as she set them in Availability & Booking */}
          {practitioner.banner_url && (
            <div
              className="bp-hero-banner"
              data-testid="practitioner-hero-banner"
              style={{ backgroundImage: cssUrl(absUrl(practitioner.banner_url)) }}
            />
          )}

          {/* Hero */}
          <div className="bp-practitioner-hero">
            <div className="bp-hero-avatar" data-testid="practitioner-hero-avatar">
              {practitioner.avatar_url ? (
                <img src={practitioner.avatar_url} alt={practitioner.display_name || 'Practitioner'} />
              ) : (
                (practitioner.display_name || 'P')[0]
              )}
            </div>
            <div>
              <p className="bp-hero-name">{practitioner.display_name || 'Practitioner'}</p>
              <p className="bp-hero-bio">{practitioner.bio || 'Book a session with this practitioner'}</p>
              {practitioner.rating_avg > 0 && (
                <div className="bp-rating">
                  <Star size={13} fill="currentColor" />
                  {practitioner.rating_avg.toFixed(1)} ({practitioner.rating_count} reviews)
                </div>
              )}
              {/* No cancellation terms here. A refund policy is the wrong first thing to read
                  about a practitioner (owner, Jun 2026: "it is cold to see it here") — they are
                  shown next to the pay button, where the money is. */}
            </div>
          </div>

          {/* Mode 3: External booking — her own session list where she keeps a link per
              offering, otherwise the single practice-wide link. */}
          {hasExternal && (externalSessions.length > 0 || practitioner.external_url) && (
            <>
              <p className="bp-section-title">Book via {practitioner.external_agent || "external platform"}</p>
              {externalSessions.length > 0 ? (
                <div data-testid="external-session-list">
                  <p className="bp-ext-note">
                    These sessions are booked and paid for on{" "}
                    {practitioner.external_agent || "the practitioner's own booking system"}, so
                    11/11's cancellation terms, tipping and reviews don't apply to them.
                  </p>
                  {externalSessions.map(st => (
                    <div key={st.id} className="bp-ext-session" data-testid={`external-session-${st.id}`}>
                      <div>
                        <p className="bp-ext-session-name">{st.name || st.title}</p>
                        <div className="bp-ext-session-meta">
                          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <Clock size={11} /> {st.healing_duration || st.duration_minutes || 60} min
                          </span>
                          {st.price > 0 && <span>${st.price}</span>}
                          {st.description && (
                            <button
                              type="button"
                              onClick={() => setSessionDetails(st)}
                              style={{
                                background: "none", border: "none", padding: 0, cursor: "pointer",
                                fontSize: 12, fontWeight: 600, color: "var(--forest)",
                                textDecoration: "underline", fontFamily: "inherit",
                              }}
                              data-testid={`external-session-details-${st.id}`}
                            >
                              Details
                            </button>
                          )}
                        </div>
                      </div>
                      <button
                        className="bp-ext-session-btn"
                        onClick={() => openBooking(
                          st.external_url,
                          `${st.name || st.title} · ${practitioner.external_agent || "External booking"}`,
                        )}
                        data-testid={`external-session-book-${st.id}`}
                      >
                        <ExternalLink size={14} />
                        {opensInNewTab(st.external_url) ? "Book ↗" : "Book"}
                      </button>
                    </div>
                  ))}
                  {practitioner.external_url && (
                    <button
                      className="bp-ext-all-link"
                      onClick={() => openBooking(practitioner.external_url)}
                      data-testid="booking-external-all-btn"
                    >
                      See everything {practitioner.display_name || 'they'} offer
                      {` on ${practitioner.external_agent || "their booking page"}`}
                      {opensInNewTab(practitioner.external_url) ? " ↗" : " →"}
                    </button>
                  )}
                </div>
              ) : (
                <div className="bp-external-panel">
                  <div className="bp-external-icon"><ExternalLink size={24} /></div>
                  <p className="bp-external-title">
                    {practitioner.display_name || 'Practitioner'} uses {practitioner.external_agent || "an external booking system"}
                  </p>
                  <p className="bp-external-desc">
                    {opensInNewTab(practitioner.external_url)
                      ? `${practitioner.external_agent || "Their booking system"} opens in a new tab — this page stays open behind it.`
                      : "Clicking below opens their booking page. 11/11 Collective Connection stays active in the background."}
                  </p>
                  <button className="bp-external-btn" onClick={() => openBooking(practitioner.external_url)} data-testid="booking-external-btn">
                    <ExternalLink size={15} />
                    {opensInNewTab(practitioner.external_url)
                      ? `Open ${practitioner.external_agent || "booking page"} ↗`
                      : "Open booking page"}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Mode 1: In-app booking */}
          {hasInApp && (
            <>
              {/* Session type selector (if practitioner has session types) */}
              {hasSessions && (
                <>
                  <p className="bp-section-title" style={{ marginTop: hasExternal ? 20 : 0 }}>
                    Choose a session
                  </p>
                  <div className="bp-session-options">
                    {practitioner.session_types.filter(st => !st.external_url).map(st => (
                      <div key={st.id}>
                        <div
                          className={`bp-session-option ${selectedSession?.id === st.id && !usePackage ? "selected" : ""}`}
                          onClick={() => {
                            // A resting offering stays listed so they can see it exists, and
                            // says so rather than silently refusing when they try to book.
                            if (isAsleep(st)) return;
                            setSelectedSession(prev => prev?.id === st.id ? null : st);
                            setUsePackage(null);
                          }}
                          style={isAsleep(st) ? { opacity: 0.6, cursor: "default" } : undefined}
                          data-testid={`session-type-${st.id}`}
                        >
                          <div>
                            <p className="bp-session-option-name">{st.name || st.title}</p>
                            <div className="bp-session-option-meta">
                              {isAsleep(st) && (
                                <span style={{ fontWeight: 600, color: "var(--ink-light, #6b6b63)" }}
                                      data-testid={`session-asleep-${st.id}`}>
                                  {sleepNote(st)}
                                </span>
                              )}
                              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <Clock size={11} /> {st.healing_duration || st.duration_minutes || 60} min
                              </span>
                              <span data-testid={`session-location-label-${st.id}`}>
                                {bpLocationLine(st)}
                              </span>
                              {st.description && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setSessionDetails(st); }}
                                  style={{
                                    background: "none", border: "none", padding: 0, cursor: "pointer",
                                    fontSize: 12, fontWeight: 600, color: "var(--forest)",
                                    textDecoration: "underline", fontFamily: "inherit",
                                  }}
                                  data-testid={`session-details-link-${st.id}`}
                                >
                                  Details
                                </button>
                              )}
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <span className="bp-session-option-price">${st.price}</span>
                            <div style={{
                              width: 20, height: 20,
                              borderRadius: "50%",
                              border: `2px solid ${selectedSession?.id === st.id && !usePackage ? "var(--forest)" : "var(--border)"}`,
                              background: selectedSession?.id === st.id && !usePackage ? "var(--forest)" : "transparent",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              {selectedSession?.id === st.id && !usePackage && <Check size={11} color="white" />}
                            </div>
                          </div>
                        </div>

                        {/* Package option for this session type */}
                        {st.offer_as_package && (
                          <div
                            style={{
                              marginTop: -1, marginBottom: 8, marginLeft: 12, marginRight: 12,
                              padding: '10px 14px',
                              border: `1.5px solid ${selectedSession?.id === st.id && usePackage === 'buy' ? 'var(--forest)' : '#d0e8d8'}`,
                              borderRadius: '0 0 10px 10px',
                              background: selectedSession?.id === st.id && usePackage === 'buy' ? 'rgba(27,58,45,0.04)' : '#f0f8f4',
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                            }}
                            onClick={() => { setSelectedSession(st); setUsePackage('buy'); setSelectedSlots([]); }}
                            data-testid={`package-option-${st.id}`}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--forest)' }}>
                                  {st.package_size || 4}-session package
                                </span>
                                <span style={{ fontSize: 12, color: 'var(--ink-mid)', marginLeft: 8 }}>
                                  ${st.package_price || (st.price * (st.package_size || 4))}
                                  {st.package_price && st.package_price < st.price * (st.package_size || 4) && (
                                    <span style={{ color: 'var(--forest)', fontWeight: 600, marginLeft: 6 }}>
                                      Save ${((st.price * (st.package_size || 4)) - st.package_price).toFixed(2)}
                                    </span>
                                  )}
                                </span>
                              </div>
                              <div style={{
                                width: 20, height: 20, borderRadius: "50%",
                                border: `2px solid ${selectedSession?.id === st.id && usePackage === 'buy' ? "var(--forest)" : "var(--border)"}`,
                                background: selectedSession?.id === st.id && usePackage === 'buy' ? "var(--forest)" : "transparent",
                                display: "flex", alignItems: "center", justifyContent: "center",
                              }}>
                                {selectedSession?.id === st.id && usePackage === 'buy' && <Check size={11} color="white" />}
                              </div>
                            </div>
                            <p style={{ fontSize: 11, color: 'var(--ink-light)', margin: '4px 0 0' }}>
                              Pay once, book {st.package_size || 4} sessions over {st.package_expiry_days || 90} days
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Active packages for this practitioner */}
                  {myPackages.filter(p => p.vendor_id === practitioner.vendor_id && p.sessions_remaining > 0).length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-mid)', marginBottom: 6, fontFamily: "'Outfit', sans-serif" }}>
                        Your active packages
                      </p>
                      {myPackages
                        .filter(p => p.vendor_id === practitioner.vendor_id && p.sessions_remaining > 0)
                        .map(pkg => (
                          <div
                            key={pkg.id}
                            style={{
                              padding: '10px 14px',
                              border: `1.5px solid ${usePackage === pkg.id ? 'var(--forest)' : '#d0e8d8'}`,
                              borderRadius: 10,
                              background: usePackage === pkg.id ? 'rgba(27,58,45,0.04)' : '#f0f8f4',
                              cursor: 'pointer',
                              marginBottom: 6,
                              transition: 'all 0.15s',
                            }}
                            onClick={() => {
                              const st = practitioner.session_types.find(s => s.id === pkg.session_type_id);
                              setSelectedSession(st || null);
                              setUsePackage(pkg.id);
                              setSelectedSlots([]);
                            }}
                            data-testid={`use-package-${pkg.id}`}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--forest)' }}>
                                  {pkg.session_type_name}
                                </span>
                                <span style={{
                                  marginLeft: 8, fontSize: 11, fontWeight: 600, color: 'white',
                                  background: 'var(--forest)', borderRadius: 10, padding: '2px 8px',
                                }}>
                                  {pkg.sessions_remaining} of {pkg.sessions_total} remaining
                                </span>
                              </div>
                              <div style={{
                                width: 20, height: 20, borderRadius: "50%",
                                border: `2px solid ${usePackage === pkg.id ? "var(--forest)" : "var(--border)"}`,
                                background: usePackage === pkg.id ? "var(--forest)" : "transparent",
                                display: "flex", alignItems: "center", justifyContent: "center",
                              }}>
                                {usePackage === pkg.id && <Check size={11} color="white" />}
                              </div>
                            </div>
                            <p style={{ fontSize: 11, color: 'var(--ink-light)', margin: '4px 0 0' }}>
                              Expires {new Date(pkg.expires_at).toLocaleDateString()}
                              {' — No payment needed, use your credit'}
                            </p>
                          </div>
                        ))
                      }
                    </div>
                  )}
                </>
              )}

              {/* Basket stays put when you switch session types */}
              {selectedSlots.length > 0 && (
                <div
                  data-testid="basket-summary-banner"
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    gap: 10, marginBottom: 12, padding: '10px 14px', borderRadius: 10,
                    background: 'rgba(27,58,45,0.05)', border: '1px solid var(--border)',
                    fontSize: 13, color: 'var(--ink-mid)', fontFamily: "'Outfit', sans-serif",
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <CalendarCheck size={14} style={{ color: 'var(--forest)' }} />
                    <strong style={{ color: 'var(--ink)' }}>
                      {selectedSlots.length} session{selectedSlots.length > 1 ? 's' : ''} selected
                    </strong>
                    <span style={{ color: 'var(--ink-light)' }}>· kept as you switch session types</span>
                  </span>
                  {totalPrice > 0 && (
                    <strong style={{ color: 'var(--forest)' }}>${totalPrice.toFixed(2)}</strong>
                  )}
                </div>
              )}

              {/* Calendar: Show for all in-app practitioners */}
              <p className="bp-section-title">
                {usePackage === 'buy'
                  ? 'Ready to purchase'
                  : hasSessions ? (selectedSession ? 'Choose a date & time' : 'Select a session above, then choose a date') : 'Choose a date & time'
                }
              </p>

              {usePackage === 'buy' && selectedSession ? (
                <div style={{
                  background: '#f0f8f4',
                  border: '1px solid #d0e8d8',
                  borderRadius: 12,
                  padding: 20,
                  textAlign: 'center',
                  marginBottom: 16,
                }}>
                  <CreditCard size={24} style={{ opacity: 0.5, display: 'block', margin: '0 auto 8px', color: 'var(--forest)' }} />
                  <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--forest)', margin: '0 0 4px' }}>
                    {selectedSession.package_size || 4}-session package: {selectedSession.name}
                  </p>
                  <p style={{ fontSize: 13, color: 'var(--ink-mid)', margin: '0 0 8px' }}>
                    ${selectedSession.package_price || (selectedSession.price * (selectedSession.package_size || 4))} total
                    {selectedSession.package_price && selectedSession.package_price < selectedSession.price * (selectedSession.package_size || 4)
                      ? ` (${((selectedSession.price * (selectedSession.package_size || 4)) - selectedSession.package_price).toFixed(2)} savings)`
                      : ''
                    }
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--ink-light)' }}>
                    Card processing (3% + $0.30) is added at checkout and shown before you pay.
                    After purchase, you can book individual sessions using your credits — no payment needed each time.
                    Credits expire in {selectedSession.package_expiry_days || 90} days.
                  </p>
                </div>
              ) : (hasSessions && !selectedSession) ? (
                <div style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  padding: 20,
                  textAlign: 'center',
                  color: 'var(--ink-light)',
                  fontSize: 14,
                }}>
                  <Calendar size={24} style={{ opacity: 0.4, display: 'block', margin: '0 auto 8px' }} />
                  Please select a session type above to view available times.
                </div>
              ) : loadingSlots ? (
                <div style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  padding: 24,
                  textAlign: 'center',
                  color: 'var(--ink-light)',
                  fontSize: 14,
                }}>
                  <Loader size={20} style={{ animation: 'spin 1s linear infinite', display: 'block', margin: '0 auto 8px' }} />
                  Loading available times...
                </div>
              ) : availableDates.size === 0 ? (
                <div style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  padding: 24,
                  textAlign: 'center',
                  color: 'var(--ink-light)',
                  fontSize: 14,
                }}>
                  <Calendar size={24} style={{ opacity: 0.4, display: 'block', margin: '0 auto 8px' }} />
                  No available times in the next 30 days.
                </div>
              ) : (
                <div style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 16,
                }}>
                  {/* Mini calendar grid */}
                  <BookingCalendar
                    availableDates={availableDates}
                    selectedDate={selectedDate}
                    onSelectDate={(d) => { setSelectedDate(d); setSelectedSlot(null); }}
                  />

                  {/* Time slots for selected date */}
                  {selectedDate && (
                    <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                      <p style={{
                        fontSize: 13, fontWeight: 600, color: 'var(--ink)',
                        marginBottom: 12, fontFamily: "'Outfit', sans-serif"
                      }}>
                        {slotsForDate.length > 0
                          ? `Available times for ${slotsForDate[0]?.date_label || selectedDate}`
                          : 'No times available for this date'}
                        {slotsForDate.length > 0 && practitioner.timezone && (
                          <span style={{ fontWeight: 400, color: 'var(--ink-light)' }}
                                data-testid="inapp-booking-timezone">
                            {" "}· shown in {bpTzLabel(practitioner.timezone)}
                          </span>
                        )}
                      </p>
                      {slotsForDate.length > 0 && slotsForDate.every(s => slotConflict(s)) && (
                        <p
                          data-testid="slot-day-blocked-notice"
                          style={{ fontSize: 12, color: 'var(--ink-light)', marginBottom: 10, lineHeight: 1.5 }}
                        >
                          {slotConflict(slotsForDate[0])} — pick another date, or remove it from your selected sessions below.
                        </p>
                      )}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                        gap: 8,
                      }}>
                        {slotsForDate.map(slot => {
                          const selected = isSlotSelected(slot);
                          const conflict = slotConflict(slot);
                          return (
                            <button
                              key={slot.datetime_utc}
                              onClick={() => { if (!conflict) toggleSlot(slot); }}
                              disabled={!!conflict}
                              title={conflict || undefined}
                              data-testid={`slot-${slot.time_label.replace(/\s/g, '-')}`}
                              style={{
                                padding: '10px 8px',
                                borderRadius: 8,
                                border: `1.5px solid ${selected ? 'var(--forest)' : 'var(--border)'}`,
                                background: selected ? 'rgba(27,58,45,0.06)' : 'var(--surface)',
                                color: conflict ? 'var(--ink-light)' : (selected ? 'var(--forest)' : 'var(--ink)'),
                                opacity: conflict ? 0.45 : 1,
                                textDecoration: conflict ? 'line-through' : 'none',
                                fontFamily: "'Outfit', sans-serif",
                                fontSize: 13,
                                fontWeight: selected ? 600 : 400,
                                cursor: conflict ? 'not-allowed' : 'pointer',
                                transition: 'all 0.12s',
                                position: 'relative',
                              }}
                            >
                              {slot.time_label}
                              {selected && (
                                <Check size={10} style={{ position: 'absolute', top: 3, right: 3, color: 'var(--forest)' }} />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Payment form (shown after booking a paid session) */}
              {paymentStep && (
                <PaymentForm
                  clientSecret={paymentStep.clientSecret}
                  amount={paymentStep.amount}
                  breakdown={paymentStep.breakdown}
                  sessionTitle={paymentStep.title}
                  cancellationPolicy={practitioner.cancellation_policy}
                  onSuccess={() => {
                    setChargedTotal(paymentStep.amount / 100);
                    setPaymentStep(null);
                    setConfirmed(true);
                    // Tell the server the card went through — the practitioner is not notified
                    // until it has, so this is what turns a held slot into a real request.
                    if (paymentStep.bookingId) {
                      apiFetch(`/bookings/${paymentStep.bookingId}/payment-confirmed`, { method: "POST" }).catch(() => {});
                    }
                    if (paymentStep.bookingIds) {
                      paymentStep.bookingIds.forEach(id => {
                        apiFetch(`/bookings/${id}/payment-confirmed`, { method: "POST" }).catch(() => {});
                      });
                    }
                    // Refresh packages in case a package was purchased
                    apiFetch("/bookings/my-packages").then(setMyPackages).catch(() => {});
                  }}
                  onCancel={() => {
                    // Cancel the booking(s) if payment is abandoned
                    if (paymentStep.bookingId) {
                      apiFetch(`/bookings/${paymentStep.bookingId}/cancel`, { method: "PATCH" }).catch(() => {});
                    }
                    if (paymentStep.bookingIds) {
                      paymentStep.bookingIds.forEach(id => {
                        apiFetch(`/bookings/${id}/cancel`, { method: "PATCH" }).catch(() => {});
                      });
                    }
                    setPaymentStep(null);
                  }}
                />
              )}

              {/* Selected sessions summary */}
              {!paymentStep && selectedSlots.length > 0 && (
                <div style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 12,
                }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 10, fontFamily: "'Outfit', sans-serif" }}>
                    Selected sessions ({selectedSlots.length})
                  </p>
                  {selectedSlots.map((s, i) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      fontSize: 13, color: 'var(--ink-mid)', padding: '6px 0',
                      borderBottom: i < selectedSlots.length - 1 ? '1px solid var(--border)' : 'none',
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }} data-testid={`cart-slot-${s.key}`}>
                        <CalendarCheck size={12} />
                        <span>
                          <strong style={{ color: 'var(--ink)' }}>{s.session_name}</strong>
                          {' — '}{s.date_label} at {s.time_label}
                          {s.price > 0 ? ` · $${s.price.toFixed(2)}` : ' · Free'}
                        </span>
                      </span>
                      <button
                        onClick={() => toggleSlot(s)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--ink-light)', fontSize: 11, padding: '2px 6px',
                        }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {totalPrice > 0 && (
                    <>
                      <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        fontSize: 13, marginTop: 10, paddingTop: 8,
                        borderTop: '1px solid var(--border)', color: 'var(--ink-mid)',
                      }}>
                        <span>Sessions</span>
                        <span>${totalPrice.toFixed(2)}</span>
                      </div>
                      <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        fontSize: 13, color: 'var(--ink-mid)', padding: '2px 0',
                      }} data-testid="cart-processing-row">
                        <span>Card processing (3% + $0.30)</span>
                        <span>${cartProcessing.toFixed(2)}</span>
                      </div>
                      <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        fontSize: 14, fontWeight: 600, marginTop: 6, paddingTop: 6,
                        borderTop: '1px solid var(--border)', color: 'var(--forest)',
                      }} data-testid="cart-total">
                        <span>Total due today</span>
                        <span>${(totalPrice + cartProcessing).toFixed(2)}</span>
                      </div>
                      <p style={{ fontSize: 11, color: 'var(--ink-light)', marginTop: 6, lineHeight: 1.5 }}>
                        Card processing is what the payment network charges to take a card — it is added
                        on top so your practitioner receives their full rate. Sales tax, where it applies,
                        is calculated at payment.
                      </p>
                    </>
                  )}
                  {perSessionPrice > 0 && taxEnabled && (
                    <div style={{ marginTop: 12 }}>
                      <BillingZipField
                        value={billingZip}
                        onChange={setBillingZip}
                        testId="booking-billing-zip-input"
                      />
                    </div>
                  )}
                  {perSessionPrice > 0 && (
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: 8, marginTop: 12,
                      fontSize: 12, color: 'var(--ink-mid)', cursor: 'pointer',
                    }} data-testid="save-card-checkbox">
                      <input
                        type="checkbox"
                        checked={saveCard}
                        onChange={e => setSaveCard(e.target.checked)}
                        style={{ accentColor: 'var(--forest)' }}
                      />
                      Save this card for future bookings
                    </label>
                  )}
                  {savedCards.length > 0 && (
                    <div style={{
                      marginTop: 8, fontSize: 12, color: 'var(--ink-light)',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <CreditCard size={12} />
                      Saved: {savedCards[0].brand} ****{savedCards[0].last4} (exp {savedCards[0].exp_month}/{savedCards[0].exp_year})
                    </div>
                  )}
                </div>
              )}

              {!paymentStep && (
                <button
                  className="bp-book-btn"
                  onClick={bookSession}
                  disabled={(selectedSlots.length === 0 && usePackage !== 'buy') || booking}
                  data-testid="booking-confirm-btn"
                >
                  {booking
                    ? <><Loader size={16} style={{ animation: "spin 1s linear infinite" }} /> Booking...</>
                    : usePackage === 'buy' && selectedSession
                      ? <>
                          <CreditCard size={16} />
                          Purchase {selectedSession.package_size || 4}-session package — ${(() => {
                            const pkg = selectedSession.package_price || (selectedSession.price * (selectedSession.package_size || 4));
                            return (pkg + estProcessing(pkg)).toFixed(2);
                          })()}
                        </>
                      : usePackage && usePackage !== 'buy' && selectedSlots.length === 1
                        ? <>
                            <CalendarCheck size={16} />
                            Book with package credit (no charge)
                          </>
                        : selectedSlots.length > 1
                          ? <>
                              <CalendarCheck size={16} />
                              Book {selectedSlots.length} sessions{totalPrice > 0 ? ` — $${(totalPrice + cartProcessing).toFixed(2)}` : ''}
                            </>
                          : selectedSlots.length === 1
                            ? <>
                                <CalendarCheck size={16} />
                                {selectedSession
                                  ? `Book ${selectedSession.name || selectedSession.title} — ${perSessionPrice > 0 ? '$' + (totalPrice + cartProcessing).toFixed(2) : 'Free'}`
                                  : `Book ${selectedSlots[0].date_label} at ${selectedSlots[0].time_label}`
                                }
                              </>
                            : "Select a date & time to continue"
                  }
                </button>
              )}
            </>
          )}

          {/* Mode 2: Public widget link */}
          {hasPublic && (
            <div style={{
              marginTop: 16,
              padding: "14px 16px",
              background: "var(--gold-bg)",
              border: "1px solid #e0cc90",
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 13,
              color: "#7a5a10",
            }}>
              <Globe size={16} />
              <span>
                Public booking page also available — shareable with anyone.
              </span>
            </div>
          )}
        </div>

        {/* WebView overlay */}
        {showWebView && (
          <ExternalWebView
            practitioner={practitioner}
            url={showWebView.url}
            label={showWebView.label}
            onClose={() => setShowWebView(null)}
          />
        )}
      </div>
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BookingsPortal({ user, onUpgrade }) {
  const [accessCheck, setAccessCheck] = useState(null);
  const [selected, setSelected] = useState(null);
  const [practitioners, setPractitioners] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [donateTarget, setDonateTarget] = useState(null); // { vendor_id, name }

  useEffect(() => {
    apiFetch("/bookings/access").then(setAccessCheck).catch(() => {
      setAccessCheck({ allowed: false, user_tier: "free" });
    });
    apiFetch("/bookings/practitioners").then(setPractitioners).catch(() => []);
  }, []);

  const filteredPractitioners = practitioners.filter(p => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const nameMatch = (p.display_name || "").toLowerCase().includes(q);
    const sessionMatch = (p.session_types || []).some(
      st => ((st.name || st.title || "").toLowerCase()).includes(q)
    );
    return nameMatch || sessionMatch;
  });

  if (!accessCheck) return null;

  if (!accessCheck.allowed) {
    return (
      <>
        <style>{styles}</style>
        <div className="bp-root">
          <TierGate onUpgrade={onUpgrade || (() => alert("Navigate to upgrade page"))} />
        </div>
      </>
    );
  }

  if (selected) {
    return (
      <div className="bp-root">
        <PractitionerDetail
          practitioner={selected}
          onBack={() => setSelected(null)}
        />
      </div>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="bp-root">
        <PendingReviewsBanner />
        <div className="bp-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1>Sessions & Coaching</h1>
              <p>Book 1:1 time with practitioners on 11/11 Collective Connection</p>
            </div>
            <a
              href="/dashboard/my-bookings"
              data-testid="bookings-portal-my-bookings-link"
              style={{
                flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px',
                borderRadius: 10,
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                color: 'var(--forest)',
                fontSize: 13,
                fontWeight: 600,
                textDecoration: 'none',
                fontFamily: "'Outfit', sans-serif",
              }}
            >
              <CalendarCheck size={14} /> My Bookings
            </a>
          </div>
          <div className="bp-search-wrap">
            <Search size={16} className="bp-search-icon" />
            <input
              className="bp-search-input"
              type="text"
              placeholder="Search by practitioner name or session type..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              data-testid="bookings-search-input"
            />
            {searchQuery && (
              <button
                className="bp-search-clear"
                onClick={() => setSearchQuery("")}
                data-testid="bookings-search-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        <div className="bp-list">
          {/* Spotlight rail near the top of the practitioner list */}
          <SpotlightSlot slot="bookings_rail" variant="rail" theme="light" className="mb-2" />
          {practitioners.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 24px', color: 'var(--ink-light)' }}>
              <CalendarCheck size={40} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: 'var(--ink)', marginBottom: 8 }}>
                No practitioners available yet
              </p>
              <p style={{ fontSize: 13, maxWidth: 400, margin: '0 auto' }}>
                Practitioners will appear here once they set up their booking availability. Check back soon.
              </p>
            </div>
          )}
          {practitioners.length > 0 && filteredPractitioners.length === 0 && (
            <div className="bp-no-results">
              <p>No practitioners match "{searchQuery}"</p>
              <p>Try a different name or session type</p>
            </div>
          )}
          {filteredPractitioners.flatMap((p, idx) => {
            const primaryMode = (p.modes_enabled || ["in_app"])[0];
            const ModeIcon = MODE_CONFIG[primaryMode]?.icon || Calendar;
            const card = (
              <div
                key={p.id}
                className="bp-practitioner-card"
                onClick={() => setSelected(p)}
              >
                <div className="bp-card-top">
                  <div className="bp-avatar" data-testid={`practitioner-avatar-${p.id}`}>
                    {p.avatar_url ? (
                      <img src={p.avatar_url} alt={p.display_name || 'Practitioner'} />
                    ) : (
                      (p.display_name || 'P')[0]
                    )}
                  </div>
                  <div className="bp-card-info">
                    <p className="bp-card-name">{p.display_name || 'Practitioner'}</p>
                    <p className="bp-card-bio">
                      <span data-testid={`practitioner-bio-${p.id}`}>
                        {(() => {
                          const text = p.bio || 'Book a session with this practitioner';
                          return text.length > 50 ? `${text.slice(0, 50).trimEnd()}…` : text;
                        })()}
                      </span>
                      {(p.bio || '').length > 50 && (
                        <button
                          className="bp-bio-more"
                          onClick={(e) => { e.stopPropagation(); setSelected(p); }}
                          data-testid={`practitioner-bio-more-${p.id}`}
                        >
                          more
                        </button>
                      )}
                      <button
                        className="bp-donate-inline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDonateTarget({ vendor_id: p.vendor_id, name: p.display_name });
                        }}
                        data-testid={`donate-btn-${p.vendor_id}`}
                      >
                        <Heart size={12} /> Donate
                      </button>
                    </p>
                    <div className="bp-card-meta">
                      {p.rating_avg > 0 && (
                        <div className="bp-rating">
                          <Star size={11} fill="currentColor" />
                          {p.rating_avg.toFixed(1)}
                        </div>
                      )}
                      {p.modes_enabled.map(mode => (
                        <span key={mode} className={`bp-mode-badge bp-mode-${mode.replace("_", "")}`}>
                          {MODE_CONFIG[mode]?.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  <ChevronRight size={18} color="var(--ink-light)" style={{ flexShrink: 0, marginTop: 2 }} />
                </div>

                <div className="bp-sessions">
                  {(p.session_types || []).slice(0, 3).map(st => (
                    <div key={st.id} className="bp-session-chip">
                      <Clock size={11} />
                      {st.name || st.title} · <span className="bp-session-price">${st.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
            const nodes = [card];
            if ((idx + 1) % 6 === 0) {
              nodes.push(
                <SpotlightSlot key={`sp-bk-${idx}`} slot="bookings_interleave" variant="card" theme="light" pick={Math.floor(idx / 6)} />
              );
            }
            return nodes;
          })}
        </div>
      </div>

      {donateTarget && (
        <DonationModal
          vendorId={donateTarget.vendor_id}
          practitionerName={donateTarget.name}
          onClose={() => setDonateTarget(null)}
        />
      )}
    </>
  );
}
